import React, { useContext, useState, useEffect } from 'react';
import loadable from '@loadable/component';
import PropTypes from 'prop-types';
import { CartContext } from 'components/cart';
import useCampaign from 'hooks/useCampaign';
import classNames from 'lib/classNames';
// import GiftWithPurchaseWidget from "./GiftWithPurchaseWidget";
// import MultiGiftWithPurchaseWidget from "./MultiGiftWithPurchaseWidget";
import { isOCUProduct, getAllOCUProducts } from './CheckoutButton';
import Axios from 'axios';
import { getAddedGifts, getGoal, GetPresentableItemsList } from '../Utils';
import AddToUnlock from './AddToUnlock';
const Bundles = loadable(() => import('./Bundles'));
const Announcement = loadable(() => import('./announcement'));
const GiftWithPurchaseWidget = loadable(() => import('./GiftWithPurchaseWidget'));
const MultiGiftWithPurchaseWidget = loadable(() => import('./MultiGiftWithPurchaseWidget'));
const Badges = loadable(() => import('./badges'));

export const DefaultAutoDiscountSettings = {
    text: {
        color: {
            a: 1,
            r: 0,
            g: 45,
            b: 255
        },
        font_weight: 400,
        font_size: 0.9,
        text: '{{title}}'
    },
    background: {
        a: 1,
        r: 240,
        g: 242,
        b: 255
    },
    border: {
        a: 1,
        r: 161,
        g: 178,
        b: 255
    }
};

const CartItemsList = (props) => {
    const {
        campaigns,
        shopify_cart_items,
        cart_settings,
        isPreview,
        cart_settings: {
            announcement = {},
            modules: { checkout_button },
            badges = {}
        },
        productAddedToCart,
        discountCode,
        setCartItems,
        Utils,
        get_total,
        products
    } = useContext(CartContext);
    const [one_check_up_sell_product_id, ocu_campaign] = useCampaign(
        'one_check_up_sell',
        null,
        true
    )(campaigns, shopify_cart_items);
    const ocu_products = getAllOCUProducts(ocu_campaign);
    const filteredItems = shopify_cart_items.filter((item) => {
        const ocu_product = ocu_products.find(
            ({ product }) => item.product_id.toString() === product?.id
        );
        return ocu_product
            ? ocu_product.include_in_atu
            : !['free_gift', 'singleton_gift'].includes(item?.product_type?.toLowerCase());
    });
    const [add_to_unlock_product_id, add_to_unlock, isGlobal] = useCampaign('add_to_unlock')(
        campaigns,
        shopify_cart_items
    );
    const CAN_RENDER_ADD_TO_UNLOCK =
        add_to_unlock &&
        (add_to_unlock.products.length ||
            add_to_unlock.recommendation_type == 'shopify_recommendation' ||
            isPreview) &&
        add_to_unlock.show_suggestions &&
        (productAddedToCart({ id: add_to_unlock_product_id }) ||
            (isGlobal && shopify_cart_items.length));
    const { original, subtotal } = get_total(null, filteredItems);
    const byQuantity = add_to_unlock?.multi_goal_type === 'quantity';
    const bundlesDiscounts = Utils.getDiscount({
        campaigns,
        items: shopify_cart_items,
        getBundlesOnly: true
    });
    const rate = window.mu_stop_shopify_rate ? 1 : parseFloat(window.Shopify.currency.rate);
    const goalValue = (goal) => (byQuantity ? goal : parseInt(goal * 100 * rate));
    const totalItems = shopify_cart_items
        .filter(({ price }) => Number(price) > 0)
        .reduce((acc, cur) => acc + cur.quantity, 0);
    const getTotal = (reward_type) => {
        const total = byQuantity
            ? totalItems
            : reward_type === 'free_shipping'
            ? subtotal
            : original - bundlesDiscounts;
        return total;
    };
    const giftGoals = add_to_unlock?.goals?.filter(
        ({ reward_type, gift }) =>
            reward_type === 'gift' && (gift?.product?.title || gift?.products?.length)
    );
    const CAN_RENDER_GIFT_WIDGET =
        (add_to_unlock?.isActive &&
            giftGoals.length &&
            productAddedToCart({ id: add_to_unlock_product_id })) ||
        (isGlobal && shopify_cart_items.length);
    useEffect(() => {
        async function handleGiftCampaignChange() {
            const gifts = shopify_cart_items.filter(({ product_type }) =>
                ['free_gift', 'singleton_gift'].includes(product_type?.toLowerCase())
            );
            const { origin } = window.location;
            gifts.forEach(async (gift) => {
                const belongsToCurrentATU = !!giftGoals?.find((goal) => {
                    if (goal.gift.products) {
                        return !!goal.gift.products?.find(
                            (product) => product?.id === gift.product_id.toString()
                        );
                    }
                    return goal.gift.product?.id === gift.product_id.toString();
                });
                if (!belongsToCurrentATU) {
                    const { data } = await Axios.post(Utils.getLangPath('cart/update.js'), {
                        updates: { [gift.id]: 0 }
                    });
                    if (data?.items) {
                        setCartItems(data.items, data.cart_level_discount_applications);
                    }
                }
            });
        }
        handleGiftCampaignChange();
    }, [shopify_cart_items]);

    function getCartItems() {
        const items = GetPresentableItemsList(shopify_cart_items);
        return window.mu_dont_reverse_items ? items.reverse() : items;
    }

    return (
        <>
            <div
                id="mu-items-container"
                className={classNames('flex flex-col px-5 ', {
                    'flex-1':
                        cart_settings.product.grow ||
                        (!!CAN_RENDER_ADD_TO_UNLOCK &&
                            (add_to_unlock.fbt_position === 'above_checkout' ||
                                (!add_to_unlock.fbt_position && add_to_unlock.fbt_below)) &&
                            checkout_button.fix_checkout_container)
                })}
                style={{
                    paddingTop: 12
                }}
            >
                {!!shopify_cart_items.length &&
                    announcement?.isActive &&
                    announcement.position === 'above_products' && (
                        <Announcement announcement={announcement} />
                    )}
                {!!shopify_cart_items.length &&
                    getCartItems().map((cartItem, key) => (
                        <CartItem
                            cartItem={cartItem}
                            key={key}
                            giftGoals={giftGoals}
                            line={shopify_cart_items.length - key}
                        />
                    ))}
                {!!CAN_RENDER_GIFT_WIDGET &&
                    giftGoals.map((goal, key) => {
                        if (!goal.gift?.widget?.active) {
                            return null;
                        }
                        const { products, previous_gift } = goal.gift;
                        const hasManyGifts = products?.length;
                        // find if there is a previous goal and inject it on MultiGiftWithPurchaseWidget if the current goal removes the previous one
                        const previous_goal =
                            previous_gift === 'remove' && key > 0 && giftGoals[key - 1];
                        // find the next goal and check if it's unlocked and it's removed previous and a gift is added
                        //TODO: find all next goals with  previous_gift = remove
                        //TODO: check if at least one goal is unlocked to hide this goal
                        const next_goal =
                            key < giftGoals.length - 1 ? giftGoals[key + 1] : undefined;
                        const next_remove_previous_goals = giftGoals
                            .slice(key + 1)
                            .filter(({ gift }) => gift?.previous_gift === 'remove');
                        const oneGoalIsUnlocked = (goalObject) => {
                            const { goal: oldGoal, specific_goals, reward_type } = goalObject;
                            const goal = getGoal({
                                byQuantity,
                                oldGoal,
                                specific_goals,
                                inCents: true
                            });
                            const total = getTotal(reward_type);
                            return total >= goal;
                        };
                        if (next_remove_previous_goals.some(oneGoalIsUnlocked)) {
                            return null;
                        }
                        if (hasManyGifts)
                            return (
                                <MultiGiftWithPurchaseWidget
                                    key={key}
                                    previous_goal={previous_goal}
                                    giftGoal={goal}
                                    filteredItems={filteredItems}
                                    add_to_unlock={add_to_unlock}
                                />
                            );
                        return (
                            <GiftWithPurchaseWidget
                                key={key}
                                byQuantity={byQuantity}
                                giftGoal={goal}
                                filteredItems={filteredItems}
                                multi_goal_type={add_to_unlock.multi_goal_type}
                            />
                        );
                    })}
                {!!shopify_cart_items.length &&
                    announcement?.isActive &&
                    announcement.position === 'bellow_products' && (
                        <Announcement announcement={announcement} />
                    )}
                {/* {badges?.isActive && checkout_button.fix_checkout_container && (
          <Badges badges={badges} />
        )} */}
            </div>
            {!!CAN_RENDER_ADD_TO_UNLOCK &&
                (add_to_unlock.fbt_position === 'under_products' ||
                    add_to_unlock.fbt_position === 'above_checkout' ||
                    (!add_to_unlock.fbt_position && add_to_unlock.fbt_below)) && (
                    <div className={classNames('px-5')}>
                        <AddToUnlock {...{ add_to_unlock, add_to_unlock_product_id }} />
                    </div>
                )}
        </>
    );
};

CartItemsList.propTypes = {};

const CartItem = ({ cartItem, giftGoals }) => {
    const {
      id,
      image,
      product_title,
      product_type,
      quantity,
      line,
      line_quantity,
      price,
      product_id,
      variant_id,
      url,
      itemSoldOut,
      variant_title,
      selling_plan_allocation,
      properties,
      compare_at_price,
      variant_options,
    } = cartItem;
    const [error, setError] = useState(null);
    const [soldOut, setSoldOut] = useState(!!itemSoldOut);
    const [maxQuantity, setMaxQuantity] = useState(Infinity);
    const [selectedSellingPlanID, setSelectedSellingPlanID] = useState(
      selling_plan_allocation?.selling_plan?.id ?? null
    );
    const [plansData, setPlansData] = useState(null);
    const [loadingPlan, setLoadingPlan] = useState(false);
    const {
        campaigns,
        update_cart_item,
        Utils,
        isPreview,
        showSubscription,
        cart_settings: {
            count_compare_at,
            theme: { background },
            product,
            money_format,
            rtl,
            auto_discount = DefaultAutoDiscountSettings,
            subscription
        },
        shopify_cart_items,
        productAddedToCart,
        discountCode,
        removeFBTUpsell,
        compareAtMap,
        hasAutoDiscount,
        products
    } = useContext(CartContext);
    // const subscription = {
    //   isActive: true,
    //   text: {
    //     color: {
    //       a: 1,
    //       r: 255,
    //       g: 255,
    //       b: 255,
    //     },
    //     font_weight: 400,
    //     font_size: 0.8,
    //     text: "Upgrade to {{selling_plan_name}}",
    //   },
    //   background: {
    //     a: 1,
    //     r: 26,
    //     g: 68,
    //     b: 156,
    //   },
    //   border: {
    //     a: 1,
    //     r: 26,
    //     g: 68,
    //     b: 156,
    //   },
    // };
    const [one_check_up_sell_product_id, ocu_campaign] = useCampaign(
        'one_check_up_sell',
        null,
        true
    )(campaigns, shopify_cart_items);
    const selectedProduct = products
      ?.find(({ id }) => id == product_id);
    const selectedVariant = selectedProduct?.variants?.find(
      ({ id }) => id == variant_id
    );
    const [_, bundles] = useCampaign('bundles', product_id)(campaigns, shopify_cart_items);
    const IS_EXAMPLE = isPreview && product_id < 10;
    const IS_ONE_CHECK_PRODUCT =
        shopify_cart_items.length > 1 &&
        productAddedToCart(one_check_up_sell_product_id) &&
        isOCUProduct(ocu_campaign, product_id, IS_EXAMPLE);
    const IS_GIFT_PRODUCT = !!giftGoals?.find(
        ({ gift }) => gift?.product?.variant_id === variant_id?.toString()
    );
    const updateItem = (step) => {
        const newQuantity = (line_quantity || quantity) + step;
        update_cart_item({ line, variant_id: id, quantity: newQuantity });
        if (newQuantity === 0) removeFBTUpsell(id);
    };
    const formatText = (text) => {
        const { price, original } = getPrice();
        return text
          .replace("{{product_title}}", product_title)
          .replace("{{product_price}}", Utils.formatMoney(price, money_format))
          .replace(
            "{{original_price}}",
            original && original.toFixed(2) !== price.toFixed(2)
              ? `<del class='mu-original-price mu-opacity-75 ${
                  rtl ? "mu-ml-1" : "mu-mr-1"
                } notranslate' style='font-size:0.8em'>${Utils.formatMoney(
                  original,
                  money_format
                )}</del> `
              : ""
          );
    };
    const hasCompareAt = (variant_id, quantity, price = null) => {
        if (!count_compare_at) return false;
        const finalPrice =
            (selectedVariant && Number(selectedVariant?.compare_at_price) * 100) ??
            (compareAtMap && compareAtMap[variant_id] ? compareAtMap[variant_id] : price);
        // console.log(compareAtMap);
        // const finalPrice = compareAtMap && compareAtMap[variant_id] ? compareAtMap[variant_id] * parseFloat(window.Shopify.currency.rate) : price
        if (window.mu_handle_compareAt) return window.mu_handle_compareAt(variant_id, quantity, price, finalPrice);
        return finalPrice * quantity;
    };
    const autoDiscount = Utils.GetCartLineItemDiscount(cartItem);
    const getPrice = () => {
        let single_price = window.mu_show_single_price;
        if (autoDiscount || cartItem.discounts.length) {
            const { discounted_price, original_line_price, discounts } = cartItem;
            const price =
                original_line_price - discounts.reduce((acc, { amount }) => acc + amount, 0);
            // return { price: discounted_price * (single_price ? 1 : quantity), original: hasCompareAt(id, (single_price ? 1 : quantity)) };
            if (!hasCompareAt(id, single_price ? 1 : quantity))
                return {
                    price: single_price ? discounted_price : price,
                    original: single_price ? original_line_price / quantity : original_line_price
                };
            return {
                price: single_price ? discounted_price : price,
                original: hasCompareAt(id, single_price ? 1 : quantity)
            };
        }
        if (!bundles || discountCode?.value) {
            return {
              price:
                price * (single_price ? 1 : quantity) -
                (cartItem.discounts?.reduce(
                  (acc, { amount }) => acc + amount,
                  0
                ) ?? 0) /
                  (single_price ? quantity : 1),
              original: hasCompareAt(id, single_price ? 1 : quantity, price),
            };
        }
        const productQuantity = shopify_cart_items
          .filter(({ product_id }) => product_id == cartItem.product_id)
          .reduce((acc, { quantity }) => acc + quantity, 0);
        const selectedBundle =
            bundles.list.find((bundle) => bundle.quantity === productQuantity) ||
            Utils.sortByQuantity(bundles.list).find(
                (bundle) => bundle.quantity < productQuantity
            );
        if (!selectedBundle)
            return {
              price: price * (single_price ? 1 : quantity),
              original: hasCompareAt(id, single_price ? 1 : quantity, price),
            };
        const discountedPrice = Utils.getBundleDiscount(
            selectedBundle,
            single_price ? 1 : quantity,
            price,
            money_format
        );  
        return {
            price: discountedPrice.plain,
            original: hasCompareAt(id, single_price ? 1 : quantity, price)
        };
    };

    function formatAutoDiscount(discount) {
        const { title, amount } = discount;
        return auto_discount.text.text
            .replace('{{title}}', title)
            .replace('{{amount}}', Utils.formatMoney(amount, money_format));
    }
    function formatBundleLabel(text) {
        if(!text) return null;
        const quantity = shopify_cart_items
            .filter((i) => i.product_id === product_id)
            .reduce((acc, { quantity }) => acc + quantity, 0);
        const bundle = Utils.sortBundles(bundles.list).reduce((acc, cur) => {
            if (quantity >= cur.quantity) acc = cur;
            return acc;
        }, null);
        if (!bundle) return null;
        const { type, reward } = bundle;
        const formatted_reward =
            type === 'percentage'
                ? `${reward}%`
                : Utils.formatMoney(reward * 100, money_format, (n, p) =>
                      Utils.formatWithDelimiters(n, p, ',', '.', true)
                  );
        return text.replace('{{quantity}}', quantity).replace('{{discount}}', formatted_reward);
    }

    useEffect(() => {
      const fetchData = async () => {
        if(isPreview) {
          if(showSubscription) {
            const response = await Utils.getPlansData(8353444036775, "recharge", null, true);
            setPlansData(response);
          }
        } else {
          const subscriptionApps = [
            { name: "recharge", scriptKeyword: "rechargecdn.com" },
            { name: "seal", scriptKeyword: "sealsubscriptions.com" },
            { name: "casa", scriptKeyword: "casaapps.com" },
            { name: "subify", scriptKeyword: "subify-subscriptions" },
            { name: "appstle", scriptKeyword: "appstle-subscription" },
            { name: "recurpay", scriptKeyword: "recurpay-subscriptions" },
          ];

          const checkForAppScript = (scriptKeyword) => {
            const scripts = document.getElementsByTagName("script");
            for (let i = 0; i < scripts.length; i++) {
              if (scripts[i].src.includes(scriptKeyword)) {
                return true;
              }
            }
            return false;
          };

          let plansData = null;
          for (const app of subscriptionApps) {
            if (checkForAppScript(app.scriptKeyword)) {
              plansData = await Utils.getPlansData(
                product_id,
                app.name,
                url,
                null,
                variant_id
              );
              if (plansData) {
                setPlansData(plansData);
                break;
              }
            }
          }
        }
      };

      fetchData();
    }, [product_id, showSubscription, isPreview]);

    if (['free_gift', 'singleton_gift', 'ship_protect'].includes(product_type?.toLowerCase())) {
        return null;
    }

    function formatSubsciptionUpgrade(text="", plans_group_name, selling_plan_name) {
      return text.replace(
          /{{plans_group_name}}/g,
          plans_group_name ?? Object.keys(plansData.subscriptions)[0]
        ).replace(
          /{{selling_plan_name}}/g,
          selling_plan_name ??
            plansData.subscriptions[
              Object.keys(plansData.subscriptions)[0]
            ]?.[0]?.external_plan_name
        );
    }
    
    return IS_ONE_CHECK_PRODUCT || IS_GIFT_PRODUCT ? null : (
      <div
        data-product-id={product_id}
        data-variant-id={variant_id}
        className={classNames("cart-item mb-2")}
      >
        <div className={classNames("flex")}>
          <div
            className={classNames("item-image bg-cover bg-center block", {
              "ml-2": rtl,
              "mr-2": !rtl,
            })}
            style={{
              backgroundImage: `url(${
                window.mu_display_product_image && selectedProduct
                  ? selectedProduct.images[0].src
                  : (properties && properties["_customily-preview"]) || image
              })`,
              width: `${product.image_size}px`,
              height: `${product.image_size}px`,
            }}
          />
          <div className={classNames("flex-1 flex-col")}>
            <div
              className={classNames(
                "item-title mb-0 overflow-hidden font-bold line-clamp leading-none mb-1"
              )}
              style={{
                fontSize: Utils.em(product.title.font_size),
                color: Utils.formatColor(product.title.color),
                fontWeight: product.title.font_weight,
              }}
            >
              {isPreview || window.mu_title_unclickable ? (
                <span
                  dangerouslySetInnerHTML={{
                    __html: formatText(product.title.text),
                  }}
                />
              ) : (
                <a
                  href={`${window.origin}${url}`}
                  dangerouslySetInnerHTML={{
                    __html: formatText(product.title.text),
                  }}
                />
              )}
            </div>
            {variant_title && variant_options && !window.hide_variant_title && (
              <p className={classNames("item-variants leading-none mb-1")}>
                {variant_options.join(window.mu_variants_join_char ?? "•")}
              </p>
            )}
            {(!!autoDiscount ||
              (cartItem.discounts.length > 0 &&
                (window.mu_show_discount_when_code_only
                  ? Boolean(discountCode?.value)
                  : true))) &&
              !bundles && (
                <div
                  className={classNames(
                    "item-auto-discount text-sm p-1 rounded mb-1 flex items-center space-x-1 leading-none mb-1"
                  )}
                  style={{
                    fontSize: `${auto_discount.text.font_size}em`,
                    fontWeight: auto_discount.text.font_weight,
                    color: Utils.formatColor(auto_discount.text.color),
                    background: Utils.formatColor(auto_discount.background),
                    border: `${Utils.formatColor(
                      auto_discount.border
                    )} 1px solid`,
                  }}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    className={classNames("w-5 h-5")}
                  >
                    <path
                      fillRule="evenodd"
                      d="M5.5 3A2.5 2.5 0 003 5.5v2.879a2.5 2.5 0 00.732 1.767l6.5 6.5a2.5 2.5 0 003.536 0l2.878-2.878a2.5 2.5 0 000-3.536l-6.5-6.5A2.5 2.5 0 008.38 3H5.5zM6 7a1 1 0 100-2 1 1 0 000 2z"
                      clip-rule="evenodd"
                    />
                  </svg>
                  <span
                    className={classNames("leading-none")}
                    dangerouslySetInnerHTML={{
                      __html: formatAutoDiscount(cartItem.discounts[0]),
                    }}
                  ></span>
                </div>
              )}
            {!!bundles &&
              !soldOut &&
              !discountCode &&
              !hasAutoDiscount &&
              formatBundleLabel(bundles.text) && (
                <div
                  className={classNames(
                    "item-auto-discount text-sm p-1 rounded mb-1 flex items-center space-x-1 leading-none inline-block"
                  )}
                  style={{
                    fontSize: `${auto_discount.text.font_size}em`,
                    fontWeight: auto_discount.text.font_weight,
                    color: Utils.formatColor(
                      bundles.badge?.text || auto_discount.text.color
                    ),
                    background: Utils.formatColor(
                      bundles.badge?.background || auto_discount.background
                    ),
                    border: `${Utils.formatColor(
                      bundles.badge?.border || auto_discount.border
                    )} 1px solid`,
                  }}
                >
                  {" "}
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    className={classNames("w-5 h-5")}
                  >
                    <path
                      fillRule="evenodd"
                      d="M5.5 3A2.5 2.5 0 003 5.5v2.879a2.5 2.5 0 00.732 1.767l6.5 6.5a2.5 2.5 0 003.536 0l2.878-2.878a2.5 2.5 0 000-3.536l-6.5-6.5A2.5 2.5 0 008.38 3H5.5zM6 7a1 1 0 100-2 1 1 0 000 2z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <span
                    className={classNames("leading-none")}
                    dangerouslySetInnerHTML={{
                      __html: formatBundleLabel(bundles.text),
                    }}
                  />
                </div>
              )}
            {properties &&
              !properties["File1"] &&
              !properties["_pplr_preview"] &&
              !properties["_is_preorder"] &&
              window.show_item_properties && (
                <div className={classNames("item-properties space-y-1")}>
                  {Object.entries(properties)
                    .filter(
                      ([key, value]) =>
                        typeof value === "string" &&
                        (!key.startsWith("_") || window.show_private_properties)
                    )
                    .map(([key, value]) => (
                      <p
                        className={classNames(
                          "item-property text-sm capitalize"
                        )}
                        key={key}
                        style={{ opacity: 0.8 }}
                        data-key={key}
                        data-value={value}
                        dangerouslySetInnerHTML={{
                          __html: `<b>${key}: </b> ${value}`,
                        }}
                      ></p>
                    ))}
                </div>
              )}
            {selling_plan_allocation?.selling_plan && (
              <p className={classNames("item-selling-plan text-xs")}>
                {selling_plan_allocation?.selling_plan?.name}
              </p>
            )}
            {properties && properties["File1"] && (
              <div className={classNames("item-properties gf-file")}>
                {Object.entries(properties)
                  .filter(([key]) => key !== "_pplr_ref_variant")
                  .map(([key, value]) => (
                    <div
                      key={key}
                      className={classNames(
                        "item-property flex items-center justify-start my-1"
                      )}
                      data-key={key}
                      data-value={value}
                    >
                      <p className={classNames("text-sm")}>{key}</p>
                      {key.includes("File") ? (
                        <a href={value} target={"_blank"}>
                          <img
                            className={classNames(
                              "rounded-sm ml-12 h-12 w-12 object-cover"
                            )}
                            src={value}
                          />
                        </a>
                      ) : (
                        <p className={classNames("text-sm ml-2")}>{value}</p>
                      )}
                    </div>
                  ))}
              </div>
            )}
            {properties && properties["_pplr_preview"] && (
              <div className={classNames("item-properties pplr mt-1")}>
                {Object.entries(properties)
                  .filter(
                    ([name, value]) =>
                      name &&
                      value &&
                      !name.includes("_pplr_preview") &&
                      !name.includes("_pplr_ref_variant") &&
                      !name.includes("_svg")
                  )
                  .map(([name, value]) => (
                    <div
                      key={name}
                      className={classNames("item-property text-sm")}
                      data-key={name}
                      data-value={value}
                      dangerouslySetInnerHTML={{
                        __html: `${name.replace(
                          "See photo guide",
                          ""
                        )}: ${value}`,
                      }}
                    ></div>
                  ))}
              </div>
            )}
            {properties && properties["_is_preorder"] && (
              <div className={classNames("item-properties preorder mt-1")}>
                {Object.entries(properties)
                  .filter(
                    ([name, value]) =>
                      name &&
                      value &&
                      !name.includes("_is_preorder") &&
                      !name.includes("_preorder_locale")
                  )
                  .map(([name, value]) => (
                    <p
                      className={classNames("item-property text-sm")}
                      key={name}
                      data-key={name}
                      data-value={value}
                    >
                      <b
                        className={classNames("font-bold")}
                      >{`${name}: ${value}`}</b>
                    </p>
                  ))}
              </div>
            )}
            <div className={classNames("flex items-center")}>
              <b
                className={`${classNames(
                  "item-price flex-1 leading-none"
                )} notranslate`}
                style={{
                  fontSize: Utils.em(product.price.font_size),
                  fontWeight: product.price.font_weight,
                  color: Utils.formatColor(product.price.color),
                }}
                dangerouslySetInnerHTML={{
                  __html: formatText(product.price.text),
                }}
              />

              {!soldOut && (
                <div
                  className={`${classNames(
                    "item-quantity flex items-stretch"
                  )} notranslate`}
                >
                  <button
                    style={{
                      border: "1px solid",
                      color: Utils.formatColor(product.title.color),
                      borderColor: Utils.lighterColor(0.8, product.title.color),
                      minWidth: "unset",
                    }}
                    className={classNames(
                      "border flex-center p-1 xs:p-2 bg-transparent",
                      {
                        "rounded-r rounded-l-none": rtl,
                        "rounded-l rounded-r-none": !rtl,
                      }
                    )}
                    onClick={() => updateItem(-1)}
                    aria-label={
                      quantity === 1
                        ? "remove item from cart"
                        : "decrease item quantity by one"
                    }
                  >
                    {quantity === 1 && (
                      <svg
                        aria-hidden="true"
                        focusable="false"
                        data-icon="trash"
                        style={{ height: 12 }}
                        role="img"
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 448 512"
                      >
                        <path
                          fill="currentColor"
                          d="M0 84V56c0-13.3 10.7-24 24-24h112l9.4-18.7c4-8.2 12.3-13.3 21.4-13.3h114.3c9.1 0 17.4 5.1 21.5 13.3L312 32h112c13.3 0 24 10.7 24 24v28c0 6.6-5.4 12-12 12H12C5.4 96 0 90.6 0 84zm415.2 56.7L394.8 467c-1.6 25.3-22.6 45-47.9 45H101.1c-25.3 0-46.3-19.7-47.9-45L32.8 140.7c-.4-6.9 5.1-12.7 12-12.7h358.5c6.8 0 12.3 5.8 11.9 12.7z"
                        ></path>
                      </svg>
                    )}
                    {quantity > 1 && (
                      <svg
                        aria-hidden="true"
                        focusable="false"
                        data-icon="minus"
                        style={{ height: 12 }}
                        role="img"
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 448 512"
                      >
                        <path
                          fill="currentColor"
                          d="M424 318.2c13.3 0 24-10.7 24-24v-76.4c0-13.3-10.7-24-24-24H24c-13.3 0-24 10.7-24 24v76.4c0 13.3 10.7 24 24 24h400z"
                        ></path>
                      </svg>
                    )}
                  </button>
                  <div
                    className={classNames(
                      "px-2 border-t border-b font-bold flex-center w-8"
                    )}
                    style={{
                      color: Utils.formatColor(product.title.color),
                      borderColor: Utils.lighterColor(0.8, product.title.color),
                    }}
                  >
                    {quantity}
                  </div>
                  <button
                    style={{
                      border: "1px solid",
                      color: Utils.formatColor(product.title.color),
                      borderColor: Utils.lighterColor(0.8, product.title.color),
                      minWidth: "unset",
                    }}
                    className={classNames(
                      "border flex-center p-1 xs:p-2 bg-transparent",
                      {
                        "rounded-l rounded-r-none": rtl,
                        "rounded-r rounded-l-none": !rtl,
                        "opacity-50 cursor-not-allowed":
                          quantity + 1 > maxQuantity,
                      }
                    )}
                    disabled={quantity + 1 > maxQuantity}
                    aria-label="increase item quantity by one"
                    onClick={() => updateItem(+1)}
                  >
                    <svg
                      aria-hidden="true"
                      focusable="false"
                      data-icon="plus"
                      style={{ height: 12 }}
                      role="img"
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 448 512"
                    >
                      <path
                        fill="currentColor"
                        d="M448 294.2v-76.4c0-13.3-10.7-24-24-24H286.2V56c0-13.3-10.7-24-24-24h-76.4c-13.3 0-24 10.7-24 24v137.8H24c-13.3 0-24 10.7-24 24v76.4c0 13.3 10.7 24 24 24h137.8V456c0 13.3 10.7 24 24 24h76.4c13.3 0 24-10.7 24-24V318.2H424c13.3 0 24-10.7 24-24z"
                      ></path>
                    </svg>
                  </button>
                </div>
              )}
              {soldOut && (
                <div
                  className={classNames(
                    "item-soldout flex text-red-500 items-center"
                  )}
                >
                  <svg
                    aria-hidden="true"
                    focusable="false"
                    data-icon="ban"
                    role="img"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 512 512"
                    className={classNames("w-4 h-4", rtl ? "ml-1" : "mr-1")}
                  >
                    <path
                      fill="currentColor"
                      d="M256 8C119.034 8 8 119.033 8 256s111.034 248 248 248 248-111.034 248-248S392.967 8 256 8zm130.108 117.892c65.448 65.448 70 165.481 20.677 235.637L150.47 105.216c70.204-49.356 170.226-44.735 235.638 20.676zM125.892 386.108c-65.448-65.448-70-165.481-20.677-235.637L361.53 406.784c-70.203 49.356-170.226 44.736-235.638-20.676z"
                    ></path>
                  </svg>
                  <span>Sold out</span>
                </div>
              )}
            </div>
            <div
              className={classNames(
                "remove-item-container flex items-center hidden"
              )}
            >
              <button
                className={classNames(
                  "remove-item border flex-center p-1 xs:p-2 bg-transparent"
                )}
                onClick={() => {
                  update_cart_item({
                    line,
                    variant_id: id,
                    quantity: 0,
                  });
                }}
              >
                Remove
              </button>
            </div>
          </div>
        </div>
        {subscription &&
          subscription?.isActive &&
          !!plansData &&
          !!plansData.subscriptions && (
            <div
              className={classNames("subscription-container mt-2 mb-2 w-full")}
            >
              {!selectedSellingPlanID ? (
                loadingPlan ? (
                  <div
                    className={classNames(
                      "subscription-button text-white block m-0 pt-2 pb-2 pl-3 pr-3 w-full leading-5 cursor-pointer shadow-none text-center"
                    )}
                    data-state="upgrading"
                    style={{
                      fontSize: `${
                        subscription.upgradingText?.font_size ?? 0.8
                      }em`,
                      fontWeight:
                        subscription.upgradingText?.font_weight ?? 500,
                      lineHeight: "normal",
                      color: Utils.formatColor(
                        subscription.upgradingText?.color ?? {
                          a: 1,
                          r: 255,
                          g: 255,
                          b: 255,
                        }
                      ),
                      backgroundColor: Utils.formatColor(
                        subscription.loadingBackground ?? {
                          a: 1,
                          r: 153,
                          g: 153,
                          b: 153,
                        }
                      ),
                      border: `1px solid ${Utils.formatColor(
                        subscription.loadingBorder ?? {
                          a: 1,
                          r: 153,
                          g: 153,
                          b: 153,
                        }
                      )}`,
                    }}
                  >
                    <span>{subscription.upgradingText?.text}</span>
                  </div>
                ) : (
                  <button
                    className={classNames(
                      "subscription-button text-white block m-0 pt-2 pb-2 pl-3 pr-3 w-full leading-5 cursor-pointer shadow-none text-center"
                    )}
                    onClick={async () => {
                      const labels = Object.keys(plansData.subscriptions);
                      const plans = plansData.subscriptions[labels[0]];
                      const selling_plan_id = plans[0].external_plan_id;

                      // console.log("selling_plan_id:", selling_plan_id);
                      // console.log("upgrading...");

                      if (isPreview) {
                        setLoadingPlan(true);
                        return setTimeout(() => {
                          setSelectedSellingPlanID(selling_plan_id);
                          setLoadingPlan(false);
                        }, 1000);
                      }

                      const updateCartItem = async (retry = false) => {
                        try {
                          const { item, items } = await new Promise(
                            (resolve, reject) => {
                              update_cart_item(
                                {
                                  line,
                                  variant_id: id,
                                  quantity,
                                  selling_plan: selling_plan_id,
                                },
                                (item, items, error) => {
                                  if (error) {
                                    reject(error);
                                  } else {
                                    resolve({ item, items });
                                  }
                                }
                              );
                            }
                          );

                          // Log the successful upgrade and update state
                          // console.log("upgraded successfully");
                          setSelectedSellingPlanID(selling_plan_id);

                          // Return the items for further use if needed
                          return { item, items };
                        } catch (error) {
                          console.error("An error occurred:", error);
                          // Retry the update in case of an error if it hasn't retried already
                          if (!retry) {
                            console.log("Retrying due to error...");
                            return await updateCartItem(true);
                          }
                        } finally {
                          setLoadingPlan(false);
                        }
                      };

                      setLoadingPlan(true);

                      // Execute the update logic with an initial attempt
                      await updateCartItem();
                    }}
                    style={{
                      backgroundColor: Utils.formatColor(
                        subscription.background
                      ),
                      border: `1px solid ${Utils.formatColor(
                        subscription.border
                      )}`,
                      color: Utils.formatColor(subscription.text.color),
                      fontSize: `${subscription.text.font_size}em`,
                      fontWeight: subscription.text.font_weight,
                      lineHeight: "normal",
                    }}
                  >
                    <span>
                      {formatSubsciptionUpgrade(subscription.text.text)}
                    </span>
                  </button>
                )
              ) : loadingPlan ? (
                <div
                  className={classNames(
                    "subscription-button text-white block m-0 pt-2 pb-2 pl-3 pr-3 w-full leading-5 cursor-pointer shadow-none text-center"
                  )}
                  data-state="downgrading"
                  style={{
                    fontSize: `${
                      subscription.downgradingText?.font_size ?? 0.8
                    }em`,
                    fontWeight:
                      subscription.downgradingText?.font_weight ?? 500,
                    lineHeight: "normal",
                    color: Utils.formatColor(
                      subscription.downgradingText?.color ?? {
                        a: 1,
                        r: 255,
                        g: 255,
                        b: 255,
                      }
                    ),
                    backgroundColor: Utils.formatColor(
                      subscription.loadingBackground ?? {
                        a: 1,
                        r: 153,
                        g: 153,
                        b: 153,
                      }
                    ),
                    border: `1px solid ${Utils.formatColor(
                      subscription.loadingBorder ?? {
                        a: 1,
                        r: 153,
                        g: 153,
                        b: 153,
                      }
                    )}`,
                  }}
                >
                  <span>{subscription.downgradingText?.text}</span>
                </div>
              ) : (
                <select
                  className={classNames(
                    "subscription-frequency border-gray-200 pt-2 pb-2 pl-3 pr-5 w-full leading-5 shadow-none"
                  )}
                  style={{
                    backgroundColor: Utils.formatColor(
                      subscription.subscribedBackground ?? {
                        a: 1,
                        r: 255,
                        g: 255,
                        b: 255,
                      }
                    ),
                    border: `1px solid ${Utils.formatColor(
                      subscription.subscribedBorder ?? {
                        a: 1,
                        r: 90,
                        g: 117,
                        b: 248,
                      }
                    )}`,
                    color: Utils.formatColor(
                      subscription.subscribedText?.color ?? {
                        a: 1,
                        r: 90,
                        g: 117,
                        b: 248,
                      }
                    ),
                    fontSize: `${
                      subscription.subscribedText?.font_size ?? 0.8
                    }em`,
                    fontWeight: subscription.subscribedText?.font_weight ?? 500,
                    backgroundImage:
                      "linear-gradient(45deg, transparent 50%, #ccc 50%), linear-gradient(135deg, #ccc 50%, transparent 50%), linear-gradient(to right, #ccc, #ccc)",
                    backgroundPosition:
                      "calc(100% - 15px) calc(13px), calc(100% - 10px) calc(13px), calc(100% - 2.5em) 0.5em",
                    backgroundSize: "5px 5px, 5px 5px, 1px 1.5em",
                    backgroundRepeat: "no-repeat",
                    appearance: "none",
                    lineHeight: "normal",
                  }}
                  value={selectedSellingPlanID}
                  onChange={async (event) => {
                    const value = event.target.value;
                    const selling_plan_id = value !== "onetime" ? value : null;

                    // console.log("selling_plan_id:", selling_plan_id);

                    if (isPreview) {
                      if (!value || value === "onetime") {
                        setLoadingPlan(true);
                      }
                      return setTimeout(() => {
                        setSelectedSellingPlanID(selling_plan_id);
                        setLoadingPlan(false);
                      }, 1000);
                    }

                    const updateCartItem = async (retry = false) => {
                      try {
                        const { item, items } = await new Promise(
                          (resolve, reject) => {
                            update_cart_item(
                              {
                                line,
                                variant_id: id,
                                quantity,
                                selling_plan: selling_plan_id,
                              },
                              (item, items, error) => {
                                if (error) {
                                  reject(error);
                                } else {
                                  resolve({ item, items });
                                }
                              }
                            );
                          }
                        );

                        // If successfully updated, log the result and update state
                        // const message =
                        //   value === "onetime" ? "downgraded" : "updated";
                        // console.log(`${message} successfully`);
                        setSelectedSellingPlanID(selling_plan_id);

                        // Return the items for further use if needed
                        return { item, items };
                      } catch (error) {
                        console.error("An error occurred:", error);
                        // Retry the update in case of an error if it hasn't retried already
                        if (!retry) {
                          console.log("Retrying due to error...");
                          return await updateCartItem(true);
                        }
                      } finally {
                        setLoadingPlan(false);
                      }
                    };

                    if (!value || value === "onetime") {
                      setLoadingPlan(true);
                    }

                    // Execute the update logic with an initial attempt
                    await updateCartItem();
                  }}
                >
                  {/* One-time purchase option */}
                  {plansData.onetime.length > 0 && (
                    <optgroup label="Pay Full Price">
                      {plansData.onetime.map((plan) => (
                        <option key={plan.id} value="onetime">
                          One-time only
                        </option>
                      ))}
                    </optgroup>
                  )}

                  {/* Subscription options grouped by discount */}
                  {Object.keys(plansData.subscriptions).map((groupLabel) => (
                    <optgroup key={groupLabel} label={groupLabel}>
                      {plansData.subscriptions[groupLabel].map((plan) => (
                        <option
                          key={plan.external_plan_id}
                          value={plan.external_plan_id}
                        >
                          {formatSubsciptionUpgrade(
                            subscription.subscribedText?.text ??
                              "{{selling_plan_name}}",
                            groupLabel,
                            plan.external_plan_name
                          )}
                        </option>
                      ))}
                    </optgroup>
                  ))}
                </select>
              )}
            </div>
          )}
        {!!bundles && !soldOut && !discountCode && !hasAutoDiscount && (
          <Bundles
            id={id}
            line={line}
            key={id}
            bundles={bundles}
            soldOut={{
              get: soldOut,
              set: (v) => setSoldOut(v),
            }}
            maxQuantity={{
              get: maxQuantity,
              set: (v) => setMaxQuantity(v),
            }}
            itemQuantity={quantity}
          />
        )}
      </div>
    );
};

export default CartItemsList;
